/**
 * RoofDiagramAnnotator — constants + pure helpers (Iter230b — extracted)
 * ──────────────────────────────────────────────────────────────────────
 * Pure data + side-effect-free helpers used across the annotator. Lives
 * in its own module so the annotator's primary file stays focused on
 * canvas + state + dialogs.
 *
 * Behaviour is byte-identical to pre-Iter230b — every name kept the
 * same, just hoisted out of RoofDiagramAnnotator.js.
 */
// Each pin type uses a distinct colour that mirrors the LINE colour on the
// underlying roof template artwork so the office can match key → pin at a
// glance. Per user feedback 2026-05-16:
//   Ridge   = blue   (matches internal blue lines on the template)
//   Valley  = red    (matches dotted red lines on the template)
//   Spouting/Gutter = green (printed on outer perimeter — was slate)
//   Face m² = warm amber (cladding / painted face AREA — NOT a length)
//   Outlet  = yellow circle (downpipe outlet — POSITION only, no length;
//             captures diameter 80/65 mm + shape round/square instead)
//   Pipe    = sky-blue circle (plumbing vent / overflow penetration —
//             POSITION pin with free-text diameter in mm; e.g. 32 mm
//             toilet breather, 50 mm overflow)
//   Flue    = slate-grey circle (heating / wet-back flue penetration —
//             POSITION pin with free-text diameter in mm; e.g. 80 mm
//             gas flue, 150 mm woodburner)
//   Hip/Barge/Flashing keep their distinct colours.
export const PIN_TYPES = [
  // Iter241 — single source of truth for pin AND line colours. The
  // trace template editor's LINE_TYPES table now mirrors these hex
  // values so newly traced templates render in the exact same colour
  // as the pin tags / hover highlight (no more orange-ridge vs
  // blue-tag mismatch).
  { value: 'Ridge',    color: '#1E40AF', emoji: '▲', unit: 'm'  },  // blue
  { value: 'Valley',   color: '#9F1239', emoji: '▽', unit: 'm'  },  // red (dotted in trace editor)
  { value: 'Hip',      color: '#1E40AF', emoji: '◣', unit: 'm'  },  // blue (same family as Ridge)
  { value: 'Barge',    color: '#A16207', emoji: '▮', unit: 'm'  },  // brown
  { value: 'Spouting', color: '#15803D', emoji: '⌐', unit: 'm'  },  // green (gutter)
  { value: 'Outlet',   color: '#EAB308', emoji: '◯', unit: '',  position: true,  defaultDia: 80,  diameters: [80, 65],         shapes: ['Round', 'Square'] },  // yellow — POSITION pin (downpipe outlet)
  // Iter229u — Pipe + Flue removed. The unified `Penetration` pin
  // type below replaces both. Legacy data (if any) was wiped during
  // the database redesign — the operator only creates `Penetration`
  // pins now, with `penetration_kind:'pipe'|'flue'` driving the
  // diameter presets + cascade direction.
  { value: 'Penetration', color: '#0EA5E9', emoji: '⏺', unit: '', position: true, defaultDia: 32, diameters: [32, 40, 50, 80, 100, 125, 140, 150, 160, 175, 240], freeDia: true },  // light blue (flue + pipe in trace editor share this)
  { value: 'Flashing', color: '#111827', emoji: '═', unit: 'm'  },  // black
  { value: 'Face',     color: '#92400E', emoji: '▦', unit: 'm²' },  // dark brown — POINT-only area pin (m²)
  { value: 'Length',   color: '#92400E', emoji: '↔', unit: 'm', lineOnly: true },  // dark brown — drag-to-draw linear measurement (m)
  // Iter161 Phase B — Leak Area pin (Remedial Repairs). POSITION-only
  // marker linking a leak-area card to a location on the diagram.
  // No length; carries `leak_area_id` in `meta` so the builder can
  // round-trip the pin.id ↔ leak_area.pin_id link.
  { value: 'LeakArea', color: '#E11D48', emoji: '⚠', unit: '', position: true },
];

// Default diameter / shape for newly-dropped Outlet pins. Users can flip
// to 65 / Square in the edit dialog.
export const OUTLET_DIAMETERS = [80, 65];
export const OUTLET_SHAPES = ['Round', 'Square'];

// Ridge cap presets — drive the bidirectional length ↔ count calc on
// Ridge pins. Each option carries:
//   • klass — 'iron' | 'concrete' | 'clay' | 'tile' — drives the verify-
//     on-site warning + std-420 ref (concrete-only metrics).
//   • width — 'Standard' | 'Wide' (Iron only) — feeds the Components
//     form Wide vs Standard cascade against `component_rate_tables`.
// Iter227 — Ridge type now CASCADES by roof type. The Annotator's Ridge
// dialog filters this master list down to just the cap-types relevant to
// the appraisal's roof type (Iron → RoundTop Std/Wide; Concrete/Clay →
// the 4 tile caps; Tin Tile → Decramastic / Colortile cap lengths).
export const RIDGE_CAP_TYPES = [
  // Iron — RoundTop ridging (1.8 m sticks). Length is per-stick but the
  // calc is informational only since iron ridges are quoted by metre.
  { name: 'RoundTop Std',          length_mm: 1800, klass: 'iron', width: 'Standard' },
  { name: 'RoundTop Wide',         length_mm: 1800, klass: 'iron', width: 'Wide' },
  // Concrete / Clay tile caps — bedded over the apex of the tiles.
  { name: 'Standard Concrete',     length_mm: 420,  klass: 'concrete' },
  { name: 'Old Concrete',          length_mm: 460,  klass: 'concrete' },
  { name: 'Old Ross',              length_mm: 510,  klass: 'concrete' },
  { name: 'Clay',                  length_mm: 480,  klass: 'clay' },
  // Tin tile (Decramastic / Colortile) — pre-formed steel cap that
  // snaps over the apex; comes in 1200 + 2400 mm lengths.
  { name: 'Decramastic 1200mm',    length_mm: 1200, klass: 'tile' },
  { name: 'Decramastic 2400mm',    length_mm: 2400, klass: 'tile' },
  { name: 'Colortile 1200mm',      length_mm: 1200, klass: 'tile' },
  { name: 'Colortile 2400mm',      length_mm: 2400, klass: 'tile' },
];
// Standard ridge-cap length used as the "quoting unit" for concrete tile
// roofs. Even when a non-420 cap is actually installed, the office still
// wants to see the equivalent count at 420 mm because that's how rate
// tables are traditionally costed.
export const STD_RIDGE_CAP_MM = 420;
export const RIDGE_CAP_LOOKUP = Object.fromEntries(RIDGE_CAP_TYPES.map(c => [c.name, c]));

// Iron ridges are NOT capped — they're per-metre sticks cut to length.
// Constraints supplied by the trade:
//   • IRON_RIDGE_MAX_M — maximum stick length the mill will roll (no piece
//     may exceed this metres value, lap allowance included).
//   • IRON_RIDGE_LAP_M — overlap allowance per piece when the ridge has
//     to be divided. Operator orders this extra so each joint laps cleanly.
// Iter276-length (2026-06-28) — defaults are 8.0 m / 200 mm per the
// office. Admin can override via `PUT /api/admin/stick-rule-defaults`;
// the app bootstraps the override via `applyStickRule()` on mount.
export let IRON_RIDGE_MAX_M = 8.0;
export let IRON_RIDGE_LAP_M = 0.2;

/**
 * Runtime override for the stick-rule defaults. Called once at app
 * startup with the values returned from `/api/admin/stick-rule-defaults`.
 * Uses module-level `let` so every subsequent `calcIronRidgePieces`
 * call (annotator, Components tab) picks up the office's current
 * supplier values without a hardcode redeploy.
 */
export function applyStickRule({ max_stick_m, lap_m } = {}) {
  if (typeof max_stick_m === 'number' && max_stick_m > 0) IRON_RIDGE_MAX_M = max_stick_m;
  if (typeof lap_m === 'number' && lap_m >= 0) IRON_RIDGE_LAP_M = lap_m;
}

/**
 * Compute the iron-ridge pieces breakdown for a given ridge length.
 *   pieces      — number of equal sticks to order (≥1).
 *   per_piece   — covered length per piece (length_m / pieces).
 *   stock_each  — stock length to order per piece (= per_piece + lap when
 *                 pieces > 1; no lap for a single-piece ridge).
 *   total_stock — pieces × stock_each (= covered length + pieces × lap
 *                 when divided).
 * Bumps pieces upward when per_piece + lap would exceed the mill max so
 * no stick ever ends up longer than IRON_RIDGE_MAX_M.
 * Examples (with defaults 9 m / 200 mm):
 *   • 16 m → 2 × 8.20 m stock = 16.40 m
 *   • 24 m → 3 × 8.20 m stock = 24.60 m
 *   • 18 m → 3 × 6.20 m stock = 18.60 m (naive 2 pieces would need 9.2 m
 *           sticks, over the mill max → forced to 3 pieces)
 *   •  8 m → 1 × 8.00 m stock (single piece, no lap allowance)
 */
export const calcIronRidgePieces = (
  lengthM,
  maxM = IRON_RIDGE_MAX_M,
  lapM  = IRON_RIDGE_LAP_M,
) => {
  const L = Math.max(0, Number(lengthM) || 0);
  if (L === 0) return { pieces: 0, per_piece: 0, stock_each: 0, total_stock: 0 };
  let pieces = Math.max(1, Math.ceil(L / maxM));
  // Single piece never has a lap (no joint to overlap). For multi-piece
  // ridges, ensure every stick fits within the mill maximum after adding
  // the lap allowance.
  while (pieces > 1 && (L / pieces) + lapM > maxM + 1e-9) pieces += 1;
  const per_piece = L / pieces;
  const stock_each = per_piece + (pieces > 1 ? lapM : 0);
  const total_stock = pieces * stock_each;
  return { pieces, per_piece, stock_each, total_stock };
};

// Map an operator's `roof_type` value (free-form: "Iron", "Long Run Iron",
// "Concrete Tile", "Decramastic", etc.) → the cascade key used to filter
// RIDGE_CAP_TYPES + label the dialog. Returns one of:
//   'iron' | 'concrete' | 'clay' | 'tile' | 'unknown'
export const ridgeRoofKey = (roofType) => {
  const rt = String(roofType || '').trim().toLowerCase();
  if (!rt) return 'unknown';
  // Order matters — check longer / more-specific tokens first.
  if (rt.includes('clay'))                                                return 'clay';
  if (rt.includes('decramastic') || rt.includes('colortile') ||
      rt.includes('tin tile')   || rt.includes('tin-tile')   ||
      rt.includes('pressed tile'))                                        return 'tile';
  if (rt.includes('concrete') || rt.includes('tile'))                     return 'concrete';
  if (rt.includes('iron') || rt.includes('long run') || rt.includes('longrun') ||
      rt.includes('steel') || rt.includes('colorsteel'))                  return 'iron';
  return 'unknown';
};

// Friendly label for the Ridge-cap dialog header. Surfaces under the
// "Ridge Cap type" field so the operator instantly sees the context that's
// driving the dropdown narrowing.
export const ridgeRoofLabel = (roofType) => {
  switch (ridgeRoofKey(roofType)) {
    case 'iron':     return 'Iron · RoundTop ridging';
    case 'concrete': return 'Concrete tile roofs';
    case 'clay':     return 'Clay tile roofs';
    case 'tile':     return 'Pressed tile roofs (Decramastic / Colortile)';
    default:         return 'All ridge cap types';
  }
};

// Return the RIDGE_CAP_TYPES subset relevant to the appraisal's roof type.
// Falls back to the full list when the roof type is unknown / blank so
// existing data without a roof_type still works.
export const getRidgeOptionsForRoof = (roofType) => {
  const key = ridgeRoofKey(roofType);
  if (key === 'unknown') return RIDGE_CAP_TYPES;
  if (key === 'concrete') {
    // Concrete jobs sometimes need Clay caps too (mixed-roof restoration),
    // so include both concrete + clay options.
    return RIDGE_CAP_TYPES.filter(c => c.klass === 'concrete' || c.klass === 'clay');
  }
  if (key === 'clay') {
    // Clay jobs see clay first, then concrete fallbacks.
    return RIDGE_CAP_TYPES.filter(c => c.klass === 'clay' || c.klass === 'concrete');
  }
  return RIDGE_CAP_TYPES.filter(c => c.klass === key);
};

// Pick a sensible default for a fresh Ridge pin given the appraisal's
// roof type. Returns the option `name` (or empty string when no useful
// default exists, e.g. unknown roof).
export const defaultRidgeCapForRoof = (roofType) => {
  const opts = getRidgeOptionsForRoof(roofType);
  const key = ridgeRoofKey(roofType);
  if (key === 'iron')      return 'RoundTop Std';
  if (key === 'concrete')  return 'Standard Concrete';
  if (key === 'clay')      return 'Clay';
  if (key === 'tile')      return 'Colortile 2400mm';
  // Unknown — first option as a soft default
  return opts[0]?.name || '';
};

// Flashing sub-types — captured on Flashing pins so the office knows
// EXACTLY what kind of flashing to fabricate / order. Operators pick
// one from the dropdown on the edit dialog.
export const FLASHING_TYPES = [
  'Backtray',
  'Apron',
  'Head',
  'Skirt',
  'Change of Pitch',
  'Cowling',
  'Gutter Flashing',
  'Barge Tray',
];

// Downpipe storey presets — default pipe length is 3 m per storey. The
// operator can override on a per-pin basis for buildings that don't
// match the standard storey rule.
export const DOWNPIPE_STORIES = [
  { value: 1, label: 'Single', default_m: 3 },
  { value: 2, label: 'Double', default_m: 6 },
  { value: 3, label: '3-Storey', default_m: 9 },
];

// Iter229k — Downpipe material + bracket presets. PVC is the budget
// option; the iron families (Long Run / Maxx / Zinc / Copper / Aluminium)
// match what the merchant stocks alongside the gutter / spouting choice.
// Bracket types reflect the common NZ residential downpipe fixing
// methods — the office can extend this list later via component_rates.
export const DOWNPIPE_MATERIALS = [
  { value: 'PVC',       label: 'PVC',       family: 'pvc' },
  { value: 'Long Run',  label: 'Long Run',  family: 'iron' },
  { value: 'Maxam',     label: 'Maxam',     family: 'iron' },
  { value: 'Zinc',      label: 'Zinc',      family: 'iron' },
  { value: 'Copper',    label: 'Copper',    family: 'iron' },
  { value: 'Aluminium', label: 'Aluminium', family: 'iron' },
];
export const DOWNPIPE_BRACKETS = [
  { value: 'Standoff',    label: 'Standoff' },
  { value: 'Wraparound',  label: 'Wraparound' },
  { value: 'Saddle',      label: 'Saddle' },
  { value: 'Pin-clip',    label: 'Pin-clip' },
  { value: 'Concealed',   label: 'Concealed' },
];

// Iter229k — Dektite Type cascade rules (reference). The Pin.Pipe
// dialog (`PipeUsageFlashingBlock`) renders these as a 4-pill row;
// each option's `allow` set is gated by the appraisal's roof type
// (normalised via `ridgeRoofKey()`):
//   • Std       → Iron roofs (long-run / corrugate / trib).
//   • Retrofit  → Iron roofs (replaces failed dektite without lifting sheets).
//   • Lead Base → Concrete / Clay / Tin-Tile (lead skirt forms over caps).
//   • Ali Base  → Concrete / Clay / Tin-Tile (Aluminium variant of lead).
// The data lives on each pin as `pin.dektite_variant` ('std' | 'retrofit'
// | 'lead' | 'ali'); the UI pills are rendered inline in
// `PipeUsageFlashingBlock` so the live auto-link refresh stays
// co-located with the variant flip.

export const TYPE_LOOKUP = Object.fromEntries(PIN_TYPES.map(t => [t.value, t]));

// Parse a diameter range like "5-60MM" / "0-35MM" / "75-175MM" from an
// Aquaseal type's name. Returns [min, max] in mm, or null if no range
// could be recognised. Used to auto-suggest a Dektite that fits a given
// pipe diameter on the annotator's edit dialog.
export const parseAquasealRange = (name) => {
  if (!name) return null;
  // Handles "Dektite 80mm Toilet Breather" (single dia) and
  // "#3 DEKTITE BLACK 5-127MM WITH"  (min-max range).
  const m = name.match(/(\d+)\s*-\s*(\d+)\s*MM/i);
  if (m) return [parseInt(m[1], 10), parseInt(m[2], 10)];
  const single = name.match(/(\d+)\s*mm/i);
  if (single) {
    const v = parseInt(single[1], 10);
    return [v, v];
  }
  return null;
};

// Default rivets + silicone-%-of-tube per Aquaseal size band. These are
// SUGGESTIONS — the operator can override per pin. Bands match the
// existing component_rate_tables Dektite product names which embed the
// covered diameter range. Lead-based Aquaseals use the same bands but
// silicone-only material (no rivets) — gated separately on `is_lead`.
export const AQUASEAL_DEFAULTS = (band_max_mm, is_lead = false) => {
  if (is_lead) return { rivets_qty: 0, silicone_pct: 15 };
  if (!band_max_mm)               return { rivets_qty: 12, silicone_pct: 20 };
  if (band_max_mm <= 60)          return { rivets_qty: 8,  silicone_pct: 15 };
  if (band_max_mm <= 127)         return { rivets_qty: 12, silicone_pct: 20 };
  if (band_max_mm <= 175)         return { rivets_qty: 14, silicone_pct: 25 };
  if (band_max_mm <= 230)         return { rivets_qty: 16, silicone_pct: 30 };
  return                              { rivets_qty: 20, silicone_pct: 40 };
};

// Returns the correct unit for a given pin. Single-point pins inherit the
// type's configured unit (Face → m², everything else → m). Line pins are
// always linear metres EXCEPT when the user explicitly set them to Face
// — but with the new Face / Length split (2026-02-17), Face is now point
// only and Length is line only, so this just falls back to the type's
// declared unit.
export const unitForPin = (pin) => {
  const t = TYPE_LOOKUP[pin?.type] || {};
  const isLine = pin?.x_pct_end !== undefined && pin?.y_pct_end !== undefined;
  // Length pins are always linear metres (m), Face pins are area (m²).
  // For other types, a drag-drawn line still represents linear extent.
  if (isLine && pin?.type !== 'Face') return 'm';
  return t.unit || 'm';
};

// Preset labels — drop-down rather than free-text per user request so the
// office gets consistent quadrant/side names ("Front" not "frnt") for
// straight-line searches across appraisals. "Other…" flips to a free-
// text input so unique cases (e.g. "Above bay window") still flow.
export const LABEL_PRESETS = ['Front', 'Rear', 'Left', 'Right', 'Driveway Side'];

export const genId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
